import React, { useState, useEffect, useRef } from 'react';
import './ApplicationForm.css';
import { get, post, apiRequestRaw } from '../utils/api';
import { transformDates } from '../utils/transformDates';

// Import step components
import IDRequirements from './steps/IDRequirements';
import BirthProof from './steps/BirthProof';
import PriorMarriages from './steps/PriorMarriages';
import SocialSecurity from './steps/SocialSecurity';
import AgeRequirement from './steps/AgeRequirement';
import TimeRestriction from './steps/TimeRestriction';
import PartyAForm from './steps/PartyAForm';
import PartyBForm from './steps/PartyBForm';
import RelationshipCheck from './steps/RelationshipCheck';
import CovenantMarriage from './steps/CovenantMarriage';
import MaritalHistory from './steps/MaritalHistory';
import ReviewStep1 from './steps/ReviewStep1';
import ReviewStep2 from './steps/ReviewStep2';
import ReviewStep3 from './steps/ReviewStep3';
import ReviewStep4 from './steps/ReviewStep4';
import CompletionScreen from './steps/CompletionScreen';

const DRAFT_STORAGE_KEY_PREFIX = 'marriage_app_draft_';

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const US_DATE_REGEX = /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])\/\d{4}$/;

const toUsDate = (value) => {
  if (typeof value !== 'string' || !ISO_DATE_REGEX.test(value)) return value;
  const [year, month, day] = value.split('-');
  return `${month}/${day}/${year}`;
};

const toIsoDate = (value) => {
  if (typeof value !== 'string' || !US_DATE_REGEX.test(value)) return value;
  const [month, day, year] = value.split('/');
  const mm = month.padStart(2, '0');
  const dd = day.padStart(2, '0');
  return `${year}-${mm}-${dd}`;
};

const ApplicationForm = ({ user, onExit }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [submitError, setSubmitError] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [formData, setFormData] = useState({
    // Questionnaire responses
    hasValidID: null,
    hasBirthCertificate: null,
    hasSSN: null,
    meetsAgeRequirement: null,
    planningWithin24Hours: null,
    
    // Party A data
    partyA: {
      role: 'Groom',
      sex: 'Male',
      lastName: '',
      suffix: '',
      firstName: '',
      middleName: '',
      lastNameBeforeMarriage: '',
      residenceAddress: '',
      city: '',
      parish: '',
      state: '',
      zip: '',
      race: '',
      dateOfBirth: '',
      birthCity: '',
      birthState: '',
      birthCountry: '',
      motherName: '',
      motherBirthCity: '',
      motherBirthState: '',
      motherBirthCountry: '',
      fatherName: '',
      fatherBirthCity: '',
      fatherBirthState: '',
      fatherBirthCountry: '',
      ssn: '',
      ssnConfidential: false,
      phoneNumber: '',
      maritalHistory: {
        previousMarriages: '',
        lastMarriageEndDate: '',
        lastMarriageEndReason: '',
        education: ''
      }
    },

    // Party B data
    partyB: {
      role: 'Bride',
      sex: 'Female',
      lastName: '',
      suffix: '',
      firstName: '',
      middleName: '',
      lastNameBeforeMarriage: '',
      residenceAddress: '',
      city: '',
      parish: '',
      state: '',
      zip: '',
      race: '',
      dateOfBirth: '',
      birthCity: '',
      birthState: '',
      birthCountry: '',
      motherName: '',
      motherBirthCity: '',
      motherBirthState: '',
      motherBirthCountry: '',
      fatherName: '',
      fatherBirthCity: '',
      fatherBirthState: '',
      fatherBirthCountry: '',
      ssn: '',
      ssnConfidential: false,
      phoneNumber: '',
      maritalHistory: {
        previousMarriages: '',
        lastMarriageEndDate: '',
        lastMarriageEndReason: '',
        education: ''
      }
    },
    
    // Additional info
    isConsanguineous: null,
    isCovenantMarriage: null,
    covenantMarriageAcknowledgement: ''
  });
  const formDataRef = useRef(formData);

  const draftStorageKey = (user?.id ?? user?.email) ? `${DRAFT_STORAGE_KEY_PREFIX}${user?.id ?? user?.email}` : null;

  // Define all steps - now with section groupings
  const steps = [
    { id: 'eligibility', title: 'Eligibility', component: IDRequirements },
    { id: 'birth-proof', title: 'Proof of Birth', component: BirthProof },
    { id: 'prior-marriages', title: 'Prior Marriages', component: PriorMarriages },
    { id: 'social-security', title: 'Social Security', component: SocialSecurity },
    { id: 'age-requirement', title: 'Age Requirement', component: AgeRequirement },
    { id: 'time-restriction', title: 'Time Restriction', component: TimeRestriction },
    { id: 'applicant-info', title: 'Applicant Info', component: PartyAForm },
    { id: 'applicant-info-b', title: 'Applicant Info', component: PartyBForm },
    { id: 'relationship', title: 'Relationship', component: RelationshipCheck },
    { id: 'covenant', title: 'Covenant Marriage', component: CovenantMarriage },
    { id: 'marital-history', title: 'Marital History', component: MaritalHistory },
    { id: 'review', title: 'Review', component: ReviewStep1 },
    { id: 'review-2', title: 'Review', component: ReviewStep2 },
    { id: 'review-3', title: 'Review', component: ReviewStep3 },
    { id: 'review-4', title: 'Review', component: ReviewStep4 },
    { id: 'complete', title: 'Complete', component: CompletionScreen }
  ];

  const progressPhase = (step) => {
    if (step <= 5) return { label: 'Requirements', number: 1, total: 5, minutes: 16 };
    if (step <= 7) return { label: 'Party information', number: 2, total: 5, minutes: 12 };
    if (step <= 10) return { label: 'Marriage details', number: 3, total: 5, minutes: 7 };
    if (step <= 14) return { label: 'Review', number: 4, total: 5, minutes: 3 };
    return { label: 'Confirmation', number: 5, total: 5, minutes: 0 };
  };

  // Load saved progress on mount from database (with localStorage fallback)
  useEffect(() => {
    loadProgress();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- hydrate the draft once on mount for the active user

  const loadProgress = async () => {
    const getLocalDraft = () => {
      if (!draftStorageKey) return null;
      try {
        const stored = localStorage.getItem(draftStorageKey);
        if (!stored) return null;
        const parsed = JSON.parse(stored);
        if (parsed?.currentStep == null || !parsed?.formData) return null;
        const step = Math.min(Math.max(0, Number(parsed.currentStep) || 0), steps.length - 1);
        return { step, formData: parsed.formData };
      } catch {
        return null;
      }
    };

    try {
      // Try loading from database first (cookie-based auth)
      const data = await get('/api/applications/draft');
      if (data && data.application) {
        const { application } = data;

        // CRITICAL: Only load data from actual DRAFT applications
        // Never load from submitted, deleted, or other status applications
        if (application && application.status !== 'draft') {
          console.error('Received non-draft application:', application.status);
          // Clear localStorage and start fresh
          if (draftStorageKey) {
            localStorage.removeItem(draftStorageKey);
          }
          setCurrentStep(0);
          return;
        }

        if (application && application.form_data) {
          const parsed = JSON.parse(application.form_data);
          const dbStep = parsed.currentStep != null
            ? (parsed.currentStep > 0 ? parsed.currentStep - 1 : parsed.currentStep)
            : 0;
          const savedStep = Math.min(Math.max(0, dbStep), steps.length - 1);
          const savedFormData = parsed.formData || null;
          if (savedFormData) {
            let mergedStep = savedStep;
            let mergedFormData = migrateMaritalHistoryData(
              transformDates(savedFormData, toIsoDate)
            );

            // Prefer whichever source (DB/local) has newer step progress.
            const localDraft = getLocalDraft();
            if (localDraft && localDraft.step > mergedStep) {
              mergedStep = localDraft.step;
              mergedFormData = migrateMaritalHistoryData(localDraft.formData);
            }
            setCurrentStep(mergedStep);
            const merged = { ...formDataRef.current, ...mergedFormData };
            formDataRef.current = merged;
            setFormData(merged);
            // Also save to localStorage as cache
            if (draftStorageKey) {
              localStorage.setItem(draftStorageKey, JSON.stringify({ currentStep: mergedStep, formData: mergedFormData }));
            }
          }
        } else if (application && !application.form_data) {
          // Draft exists but has no form_data (new empty draft)
          // Clear any stale localStorage data
          if (draftStorageKey) {
            localStorage.removeItem(draftStorageKey);
          }
          // Start fresh - don't load from localStorage
          setCurrentStep(0);
        }
      } else {
        // Database load failed, don't load from localStorage
        // Start fresh to avoid loading stale submitted application data
        if (draftStorageKey) {
          localStorage.removeItem(draftStorageKey);
        }
        setCurrentStep(0);
      }
    } catch (error) {
      console.error('Failed to load from database:', error);
      // Fall back to localStorage so a network/session error doesn't wipe progress
      loadFromLocalStorage();
    }
  };

  const loadFromLocalStorage = () => {
    if (!draftStorageKey) return;
    try {
      const stored = localStorage.getItem(draftStorageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        const savedStep = parsed.currentStep != null ? Math.min(Math.max(0, parsed.currentStep), steps.length - 1) : 0;
        const savedFormData = parsed.formData || null;
        if (savedFormData) {
          setCurrentStep(savedStep);
          const merged = {
            ...formDataRef.current,
            ...migrateMaritalHistoryData(transformDates(savedFormData, toIsoDate))
          };
          formDataRef.current = merged;
          setFormData(merged);
        }
      }
    } catch (e) {
      // Invalid JSON; ignore
    }
  };

  // Migrate old flat structure to nested maritalHistory structure
  const migrateMaritalHistoryData = (data) => {
    const migrateParty = (party) => {
      if (!party) return party;

      // If already has maritalHistory, no migration needed
      if (party.maritalHistory) return party;

      // Create nested structure from flat fields
      return {
        ...party,
        maritalHistory: {
          previousMarriages: party.previousMarriages || '',
          lastMarriageEndDate: party.dateLastMarriageEnded || '',
          lastMarriageEndReason: party.reasonLastMarriageEnded || '',
          education: party.highestEducation || ''
        }
      };
    };

    return {
      ...data,
      partyA: migrateParty(data.partyA),
      partyB: migrateParty(data.partyB)
    };
  };

  const saveProgress = async (step = currentStep, data = formDataRef.current) => {
    // Save to localStorage FIRST (before transform) so progress is never lost
    if (draftStorageKey) {
      try {
        localStorage.setItem(draftStorageKey, JSON.stringify({ currentStep: step, formData: data }));
        setLastSavedAt(new Date());
      } catch (e) {
        console.error('Failed to save to localStorage:', e);
      }
    }

    // Also save to database for cross-device support
    try {
      const payload = {
        currentStep: step + 1,
        formData: transformDates(data, toUsDate)
      };
      await post('/api/applications/save-progress', payload);
      setLastSavedAt(new Date());
    } catch (err) {
      // Silently fail - localStorage has the data as backup
    }
  };

  const updateFormData = (updates) => {
    if (!updates || typeof updates !== 'object') return;
    const merged = {
      ...formDataRef.current,
      ...updates
    };
    formDataRef.current = merged;
    setFormData(merged);
  };

  const nextStep = (updates) => {
    // Some step buttons use onClick={nextStep}, which passes a click event as the first arg.
    // Never merge DOM/React events into formData.
    const looksLikeEvent =
      updates &&
      typeof updates === 'object' &&
      (typeof updates.preventDefault === 'function' ||
        'nativeEvent' in updates ||
        'currentTarget' in updates ||
        'target' in updates);
    const safeUpdates = looksLikeEvent ? null : updates;

    if (currentStep < steps.length - 1) {
      const nextStepIndex = currentStep + 1;
      setCurrentStep(nextStepIndex);
      if (safeUpdates) {
        const merged = { ...formDataRef.current, ...safeUpdates };
        formDataRef.current = merged;
        setFormData(merged);
        saveProgress(nextStepIndex, merged);
      } else {
        saveProgress(nextStepIndex, formDataRef.current);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const goToStep = (stepIndex) => {
    setCurrentStep(stepIndex);
  };

  const submitApplication = async () => {
    try {
      setSubmitError(null); // Clear any previous errors

      let payload;
      try {
        payload = JSON.stringify(transformDates(formDataRef.current, toUsDate));
      } catch (serializeError) {
        console.error('Failed to serialize form data:', serializeError);
        setSubmitError('Unable to prepare your application for submission. Please save your progress, refresh the page, and try again.');
        return;
      }

      const response = await apiRequestRaw('/api/applications/submit', {
        method: 'POST',
        body: payload
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        if (errBody.errors && errBody.errors.length > 0) {
          const messages = errBody.errors.map(e => `• ${e.field}: ${e.message}`).join('\n');
          setSubmitError(`Please correct the following before submitting:\n${messages}`);
        } else {
          setSubmitError(errBody.error || 'Submission failed. Please try again.');
        }
        return;
      }
      const result = await response.json();
      const completedFormData = { ...formDataRef.current, applicationId: result.applicationId };
      formDataRef.current = completedFormData;
      setFormData(completedFormData);

      // Success - clear localStorage draft
      if (draftStorageKey) localStorage.removeItem(draftStorageKey);

      // Note: Backend automatically handles the draft - it creates a new 'submitted'
      // application and writes to marriage_applicants. Any existing drafts are deleted.

      // Move to completion screen
      const completionStep = steps.length - 1;
      setCurrentStep(completionStep);

      // Ensure no draft is saved for the completion screen
      // by not calling saveProgress here
    } catch (error) {
      // Handle network or API errors
      setSubmitError(error.message || 'Unable to submit application. Please check your internet connection and try again.');
      console.error('Failed to submit application:', error);
    }
  };

  const CurrentStepComponent = steps[currentStep].component;
  const phase = progressPhase(currentStep);

  return (
    <div className="application-form-container">
      <header className="application-header">
        <div className="application-header-top">
          <div className="logo">
            <div className="logo-icon">
              <img
                src={`${process.env.PUBLIC_URL}/JPCC_seal_trans_white.png`}
                alt="Jefferson Parish Clerk of Court Seal"
              />
            </div>
            <div className="logo-text">
              <h1>Jon A. Gegenheimer</h1>
              <p>Jefferson Parish Clerk of Court</p>
              <p className="logo-context">Marriage License Pre-Application</p>
            </div>
          </div>
          <div className="application-actions">
            {/* Don't show Save & Exit on CompletionScreen - it has its own Return to Dashboard button */}
            {currentStep !== steps.length - 1 && (
              <button
                className="exit-btn"
                onClick={async () => {
                  await saveProgress();
                  onExit();
                }}
              >
                Save & Exit
              </button>
            )}
          </div>
        </div>
        <div className="application-progress">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
          <div className="progress-text">
            <span>{phase.label} · Section {phase.number} of {phase.total}</span>
            <span>{phase.minutes > 0 ? `About ${phase.minutes} minutes remaining` : 'Final step'}</span>
          </div>
          <div className="progress-meta" aria-live="polite">
            {lastSavedAt ? `Saved ${lastSavedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : 'Your progress is saved as you continue'}
          </div>
        </div>
      </header>

      <main className="application-content">
        <div className="step-container">
          <CurrentStepComponent
            formData={formData}
            updateFormData={updateFormData}
            nextStep={nextStep}
            prevStep={prevStep}
            goToStep={goToStep}
            submitApplication={submitApplication}
            submitError={submitError}
            onExit={onExit}
            isFirstStep={currentStep === 0}
            isLastStep={currentStep === steps.length - 1}
          />
        </div>
      </main>
    </div>
  );
};

export default ApplicationForm;
